import { useMemo, useState } from 'react';
import { Plus, Pencil } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import type { ColumnDef, FilterField } from '@vnetwork/ui/data';
import { StatusPill } from '@vnetwork/ui/data';
import {
  Badge,
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerBody,
  DrawerFooter,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useSampleContracts,
  useSampleContractMapping,
  useCreateSampleContract,
  useUpdateSampleContract,
} from '@/api/contract-samples';
import type { SampleContractRow, SampleMappingOption } from '@/api/contract-samples';
import type { ContractListParams } from '@/api/contracts';
import type { ListQueryResult } from '../components/contract-list-section';
import { ContractListSection } from '../components/contract-list-section';
import { contractStatusTone, statusLabel, CONTRACT_STATUS_OPTIONS } from '../contract-format';
import { formatDate } from '@/lib/format';
import { DateInput } from '@/components/date-input';

/* ── Cell helpers ────────────────────────────────────────────────────────── */

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;
const right = { display: 'block', textAlign: 'right' as const, whiteSpace: 'nowrap' as const };
const truncate = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
} as const;

/* ── Form types ──────────────────────────────────────────────────────────── */

interface SampleFormState {
  contract_number_id: string;
  contract_key: string;
  contract_note: string;
  contract_status: string;
  product_group_name: string;
  contract_effective_date: string;
  contract_expiry_date: string;
  sla_enable: boolean;
  nda_enable: boolean;
}

function blankSampleForm(): SampleFormState {
  return {
    contract_number_id: '',
    contract_key: '',
    contract_note: '',
    contract_status: 'active',
    product_group_name: '',
    contract_effective_date: '',
    contract_expiry_date: '',
    sla_enable: false,
    nda_enable: false,
  };
}

function fromSampleRow(row: SampleContractRow): SampleFormState {
  return {
    contract_number_id: row.contract_number_id ?? '',
    contract_key: row.contract_key ?? '',
    contract_note: row.contract_note ?? '',
    contract_status: row.contract_status ?? 'active',
    product_group_name: row.product_group_name ?? '',
    contract_effective_date: row.contract_effective_date
      ? row.contract_effective_date.slice(0, 10)
      : '',
    contract_expiry_date: row.contract_expiry_date
      ? row.contract_expiry_date.slice(0, 10)
      : '',
    sla_enable: row.sla_enable ?? false,
    nda_enable: row.nda_enable ?? false,
  };
}

/* ── Column builder ──────────────────────────────────────────────────────── */

/** Build sample columns, injecting the mapping list and an onEdit callback. */
function buildSampleColumns(
  mapping: SampleMappingOption[],
  onEdit: (row: SampleContractRow) => void,
): ColumnDef<SampleContractRow, unknown>[] {
  return [
    {
      id: 'contract_number_id',
      header: 'Contract ID',
      cell: ({ row }) => {
        const id = row.original.contract_number_id || `#${row.original.contract_id}`;
        return (
          <span
            style={{ ...truncate, color: 'var(--vn-primary-text)', fontWeight: 'var(--vn-font-weight-medium)' }}
            title={id}
          >
            {id}
          </span>
        );
      },
    },
    {
      id: 'contract_key',
      header: 'Contract Key',
      cell: ({ row }) => {
        const key = row.original.contract_key;
        if (!key) return <span style={muted}>—</span>;
        return <span style={truncate} title={key}>{key}</span>;
      },
    },
    {
      id: 'contract_status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusPill tone={contractStatusTone(row.original.contract_status)}>
          {statusLabel(row.original.contract_status)}
        </StatusPill>
      ),
    },
    {
      id: 'product_group_name',
      header: 'Product Group',
      cell: ({ row }) => {
        const code = row.original.product_group_name;
        if (!code) return <span style={muted}>—</span>;
        const label = mapping.find((m) => m.value === code)?.label ?? code;
        return (
          <Badge tone="neutral">
            <span style={truncate} title={label}>{label}</span>
          </Badge>
        );
      },
    },
    {
      id: 'attachments',
      header: 'Attachments',
      cell: ({ row }) => {
        const atts = row.original.attachments?.filter((a) => !a.attachment_closed) ?? [];
        if (atts.length === 0) return <span style={muted}>—</span>;
        const first = atts[0];
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
            <span
              style={{ ...truncate, maxWidth: 200, fontSize: 'var(--vn-font-size-sm)' }}
              title={first.attachment_file_name}
            >
              {first.attachment_file_name}
            </span>
            {atts.length > 1 && (
              <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-xs)', flexShrink: 0 }}>
                +{atts.length - 1}
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'contract_note',
      header: 'Note',
      cell: ({ row }) => {
        const note = row.original.contract_note;
        if (!note) return <span style={muted}>—</span>;
        return (
          <span
            style={{ ...truncate, color: 'var(--vn-fg-muted)', maxWidth: 280 }}
            title={note}
          >
            {note}
          </span>
        );
      },
    },
    {
      id: 'employee_name',
      header: 'Created by',
      cell: ({ row }) => {
        const name = row.original.employee_name;
        if (!name) return <span style={muted}>—</span>;
        return <span style={{ whiteSpace: 'nowrap' }}>{name}</span>;
      },
    },
    {
      id: 'updated_at',
      header: () => <span style={right}>Last updated</span>,
      cell: ({ row }) => (
        <span style={{ ...right, color: 'var(--vn-fg-muted)', fontStyle: 'italic' }}>
          {formatDate(row.original.updated_at)}
        </span>
      ),
    },
    {
      id: '_actions',
      header: '',
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="ghost"
          aria-label="Edit sample contract"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(row.original);
          }}
        >
          <Pencil size={13} />
        </Button>
      ),
    },
  ];
}

/* ── Filter fields ───────────────────────────────────────────────────────── */

const sampleFilterFields: FilterField[] = [
  {
    value: 'contract_status',
    label: 'Status',
    input: 'select',
    options: CONTRACT_STATUS_OPTIONS,
  },
];

/* ── Query adapter ───────────────────────────────────────────────────────── */

function useSampleQuery(params: ContractListParams): ListQueryResult<SampleContractRow> {
  return useSampleContracts(params);
}

/* ── Page ────────────────────────────────────────────────────────────────── */

/**
 * Contract Samples — `/contracts/samples`. Paginated list of sample/template
 * contracts with status filter, search, column visibility, create and edit.
 * Mirrors legacy `Contract/modules/Sample/components/List` columns.
 * No detail route (backend has no GET /contracts/samples/:id).
 * Sample DELETE verb not confirmed in api-v4 routes — deferred.
 */
export default function ContractSamplesPage() {
  const toast = useToast();
  const { data: mapping = [] } = useSampleContractMapping();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editRow, setEditRow] = useState<SampleContractRow | null>(null);
  const [form, setForm] = useState<SampleFormState>(blankSampleForm);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof SampleFormState, string>>>({});

  const createMutation = useCreateSampleContract();
  const updateMutation = useUpdateSampleContract(editRow?.contract_id ?? 0);

  /* ── Drawer helpers ────────────────────────────────────────────────────── */

  const openCreate = () => {
    setEditRow(null);
    setForm(blankSampleForm());
    setFormErrors({});
    setDrawerOpen(true);
  };

  const openEdit = (row: SampleContractRow) => {
    setEditRow(row);
    setForm(fromSampleRow(row));
    setFormErrors({});
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditRow(null);
    setFormErrors({});
  };

  const setField = <K extends keyof SampleFormState>(k: K, v: SampleFormState[K]) => {
    setForm((p) => ({ ...p, [k]: v }));
    setFormErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = (): boolean => {
    const errs: Partial<Record<keyof SampleFormState, string>> = {};
    if (!form.contract_number_id.trim()) errs.contract_number_id = 'Contract ID is required.';
    if (!form.product_group_name) errs.product_group_name = 'Product group is required.';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const contractPayload = {
      contract_number_id: form.contract_number_id.trim(),
      contract_key: form.contract_key || undefined,
      contract_note: form.contract_note || undefined,
      contract_status: form.contract_status,
      product_group_name: form.product_group_name,
      contract_effective_date: form.contract_effective_date || undefined,
      contract_expiry_date: form.contract_expiry_date || undefined,
      sla_enable: form.sla_enable,
      nda_enable: form.nda_enable,
    };

    if (editRow) {
      updateMutation.mutate(
        { contract: contractPayload },
        {
          onSuccess: () => {
            toast.success('Sample contract updated');
            closeDrawer();
          },
          onError: (err) => {
            const msg = err instanceof Error ? err.message : 'Failed to update sample contract';
            toast.error('Failed to update sample contract', { description: msg });
          },
        },
      );
    } else {
      createMutation.mutate(
        { contract: contractPayload },
        {
          onSuccess: () => {
            toast.success('Sample contract created');
            closeDrawer();
          },
          onError: (err) => {
            const msg = err instanceof Error ? err.message : 'Failed to create sample contract';
            toast.error('Failed to create sample contract', { description: msg });
          },
        },
      );
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const columns = useMemo(
    () => buildSampleColumns(mapping, openEdit),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapping],
  );

  /* ── Render ────────────────────────────────────────────────────────────── */

  return (
    <Container size="full">
      <PageHeader
        title="Contract Samples"
        description="Sample and template contracts used across product groups."
        style={{ marginBottom: 'var(--vn-space-4)' }}
        actions={
          <Button variant="primary" size="sm" startIcon={<Plus size={14} />} onClick={openCreate}>
            New sample
          </Button>
        }
      />

      <ContractListSection<SampleContractRow>
        useListQuery={useSampleQuery}
        columns={columns}
        getRowId={(r) => String(r.contract_id)}
        filterFields={sampleFilterFields}
        searchPlaceholder="Search sample contracts…"
        errorTitle="Failed to load sample contracts"
        emptyTitle="No sample contracts found"
        emptyDescription="Try adjusting your search or filters."
      />

      {/* Create / Edit Drawer */}
      <Drawer open={drawerOpen} onOpenChange={(o) => { if (!o) closeDrawer(); }}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{editRow ? 'Edit sample contract' : 'New sample contract'}</DrawerTitle>
            <DrawerDescription>
              {editRow
                ? `Editing ${editRow.contract_number_id || `#${editRow.contract_id}`}`
                : 'Create a new sample/template contract.'}
            </DrawerDescription>
          </DrawerHeader>

          <DrawerBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              <Field label="Contract ID" required error={formErrors.contract_number_id}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={form.contract_number_id}
                    onChange={(e) => setField('contract_number_id', e.target.value)}
                    placeholder="e.g. MHD-2026-001"
                  />
                )}
              </Field>

              <Field label="Contract key" error={formErrors.contract_key}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={form.contract_key}
                    onChange={(e) => setField('contract_key', e.target.value)}
                    placeholder="Optional short alias"
                  />
                )}
              </Field>

              <Field label="Product group" required error={formErrors.product_group_name}>
                {(fp) => (
                  <Select
                    value={form.product_group_name}
                    onValueChange={(v) => setField('product_group_name', v)}
                  >
                    <SelectTrigger {...fp}>
                      <SelectValue placeholder="Select product group" />
                    </SelectTrigger>
                    <SelectContent>
                      {mapping.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field label="Status" error={formErrors.contract_status}>
                {(fp) => (
                  <Select
                    value={form.contract_status}
                    onValueChange={(v) => setField('contract_status', v)}
                  >
                    <SelectTrigger {...fp}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTRACT_STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field label="Effective date" error={formErrors.contract_effective_date}>
                {(fp) => (
                  <DateInput
                    {...fp}
                    value={form.contract_effective_date}
                    onChange={(e) => setField('contract_effective_date', e.target.value)}
                  />
                )}
              </Field>

              <Field label="Expiry date" error={formErrors.contract_expiry_date}>
                {(fp) => (
                  <DateInput
                    {...fp}
                    value={form.contract_expiry_date}
                    onChange={(e) => setField('contract_expiry_date', e.target.value)}
                  />
                )}
              </Field>

              <Field label="Note" error={formErrors.contract_note}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={form.contract_note}
                    onChange={(e) => setField('contract_note', e.target.value)}
                    placeholder="Optional note"
                  />
                )}
              </Field>
            </div>
          </DrawerBody>

          <DrawerFooter>
            <Button variant="secondary" onClick={closeDrawer} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} loading={isSaving}>
              {editRow ? 'Save changes' : 'Create'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Container>
  );
}
